/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
// useMemo, useNavigate - unused but may be needed later
import { shopService, Shop } from '../services/shopService';
import { apiService, Category } from '../services/api';
import { getValidImageUrl } from '../utils/imageUtils';
import { useCategoryTranslation, useShopTypeTranslation } from '../utils/categoryTranslations';
import { useTranslations } from '../hooks/useTranslations';
import { useAuth } from '../contexts/AuthContext';
import CollapsibleSection from './CollapsibleSection';
import SmartSearch from './SmartSearch';
import './ShopDiscovery.css';

interface ShopDiscoveryProps {
  onSelectShop: (shop: Shop) => void;
  onViewAllShops: () => void;
  onViewShopDetails?: (shop: Shop) => void;
  initialShopType?: string; // Added prop for initial shop type filter
  categoryInfo?: {
    name?: string;
    displayName?: string;
    image?: string;
    icon?: string;
  }; // Category info from previous screen
}

const ShopDiscovery: React.FC<ShopDiscoveryProps> = ({ 
  onSelectShop, 
  onViewAllShops, 
  onViewShopDetails, 
  initialShopType,
  categoryInfo
}) => {
  // Removed unused: navigate
  const translateCategoryName = useCategoryTranslation();
  const translateShopType = useShopTypeTranslation();
  const t = useTranslations();
  const { user, isAuthenticated } = useAuth();
  const [shops, setShops] = useState<Shop[]>([]);
  const [filteredShops, setFilteredShops] = useState<Shop[]>([]);
  const [shopsWithProducts, setShopsWithProducts] = useState<Set<string>>(new Set());
  const [shopProducts, setShopProducts] = useState<{ [shopId: string]: any[] }>({});
  const [selectedType, setSelectedType] = useState(initialShopType || 'all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all'); // For level 3 category
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [level3Categories, setLevel3Categories] = useState<Category[]>([]);
  const [level2Categories, setLevel2Categories] = useState<Category[]>([]);
  const [categoriesByParent, setCategoriesByParent] = useState<{ [key: string]: Category[] }>({});
  const [favorites, setFavorites] = useState<string[]>([]);
  // Removed unused state: const [showAllShops, setShowAllShops] = useState(false);

  // Load favorite shops from API or localStorage on mount
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        if (isAuthenticated && user?.id) {
          // Load from API for authenticated users
          const shopIds = await apiService.getFavoriteShops(user.id);
          setFavorites(shopIds);
        } else {
          // Fallback to localStorage for guests
          try {
            const saved = localStorage.getItem('favorite_shops');
            setFavorites(saved ? JSON.parse(saved) : []);
          } catch {
            setFavorites([]);
          }
        }
      } catch (error) {
        console.error('Error loading favorite shops:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('favorite_shops');
          setFavorites(saved ? JSON.parse(saved) : []);
        } catch {
          setFavorites([]);
        }
      }
    };
    loadFavorites();
  }, [isAuthenticated, user?.id]);

  // Helper function to validate and normalize shop type (matching mobile app logic)
  const normalizeShopType = (type: string | undefined): Shop['type'] => {
    if (!type) return 'specialty';
    
    const normalized = type.toLowerCase().trim();
    
    // Map plural/variations to singular valid types (matching database values)
    const typeMapping: { [key: string]: Shop['type'] } = {
      'bakeries': 'bakery',
      'bakery': 'bakery',
      'baker': 'bakery',
      'groceries': 'grocery',
      'grocery': 'grocery',
      'kirana': 'grocery',
      'mart': 'grocery',
      'pharmacy': 'pharmacy',
      'pharmacies': 'pharmacy',
      'medical': 'medical',
      'electronics': 'electronics',
      'clothing': 'clothing',
      'restaurant': 'restaurant',
      'restaurants': 'restaurant',
      'specialty': 'specialty',
      'specialties': 'specialty'
    };
    
    // Check direct mapping first
    if (typeMapping[normalized]) {
      return typeMapping[normalized];
    }
    
    // Check if it matches any valid type
    const validTypes: Shop['type'][] = ['grocery', 'medical', 'electronics', 'clothing', 'pharmacy', 'specialty', 'bakery', 'restaurant'];
    if (validTypes.includes(normalized as Shop['type'])) {
      return normalized as Shop['type'];
    }
    
    // Default to specialty if no match
    return 'specialty';
  };

  const toggleFavorite = async (shopId: string) => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage for guests
      setFavorites(prev => {
        const exists = prev.includes(shopId);
        const next = exists ? prev.filter(id => id !== shopId) : [...prev, shopId];
        try { localStorage.setItem('favorite_shops', JSON.stringify(next)); } catch {}
        return next;
      });
      return;
    }

    try {
      if (favorites.includes(shopId)) {
        // Remove from favorites via API
        await apiService.removeFavoriteShop(user.id, shopId);
        const updated = favorites.filter(id => id !== shopId);
        setFavorites(updated);
        console.log('✅ Removed shop from favorites:', shopId);
      } else {
        // Add to favorites via API
        await apiService.addFavoriteShop(user.id, shopId);
        const updated = [...favorites, shopId];
        setFavorites(updated);
        console.log('✅ Added shop to favorites:', shopId);
      }
    } catch (error) {
      console.error('Error updating favorite shop:', error);
      // Fallback to localStorage on error
      setFavorites(prev => {
        const exists = prev.includes(shopId);
        const next = exists ? prev.filter(id => id !== shopId) : [...prev, shopId];
        try { localStorage.setItem('favorite_shops', JSON.stringify(next)); } catch {}
        return next;
      });
    }
  };

  // Check which shops have products
  const checkShopsWithProducts = async (shopsToCheck: Shop[]) => {
    const shopsWithProductsSet = new Set<string>();
    const productsMap: { [shopId: string]: any[] } = {};
    
    console.log(`🔍 Checking products for ${shopsToCheck.length} shops...`);
    
    // Check for specific shop names for debugging
    const petCareShop = shopsToCheck.find(s => s.name.toLowerCase().includes('pet'));
    if (petCareShop) {
      console.log(`🐾 Found Pet Care shop: "${petCareShop.name}" (ID: ${petCareShop.id}, Type: ${petCareShop.type}, Active: ${petCareShop.is_active})`);
    }
    
    const paradiseShop = shopsToCheck.find(s => s.name.toLowerCase().includes('paradise'));
    if (paradiseShop) {
      console.log(`🏝️ Found Paradise shop: "${paradiseShop.name}" (ID: ${paradiseShop.id}, Type: ${paradiseShop.type}, Active: ${paradiseShop.is_active})`);
    }
    
    const rajShop = shopsToCheck.find(s => s.name.toLowerCase().includes('raj'));
    if (rajShop) {
      console.log(`🛒 Found Raj groceries shop: "${rajShop.name}" (ID: ${rajShop.id}, Type: ${rajShop.type}, Active: ${rajShop.is_active})`);
    } else {
      console.log(`🛒 ❌ Raj groceries shop NOT found in shops list during product check`);
      console.log(`📋 Available shops:`, shopsToCheck.map(s => `${s.name} (${s.type}, active: ${s.is_active})`));
    }
    
    // Check each shop for products and store sample products
    const checkPromises = shopsToCheck.map(async (shop) => {
      try {
        const products = await apiService.getShopProducts(shop.id);
        if (products && products.length > 0) {
          shopsWithProductsSet.add(shop.id);
          // Store first 3 products for preview
          productsMap[shop.id] = products.slice(0, 3);
          if (shop.name.toLowerCase().includes('pet')) {
            console.log(`🐾 ✅ Pet Care shop "${shop.name}" has ${products.length} products`);
          }
          if (shop.name.toLowerCase().includes('paradise')) {
            console.log(`🏝️ ✅ Paradise shop "${shop.name}" has ${products.length} products - will show in Regular section`);
          }
          console.log(`✅ Shop "${shop.name}" (ID: ${shop.id}) has ${products.length} products`);
        } else {
          if (shop.name.toLowerCase().includes('pet')) {
            console.log(`🐾 ❌ Pet Care shop "${shop.name}" has no products`);
          }
          if (shop.name.toLowerCase().includes('paradise')) {
            console.log(`🏝️ ❌ Paradise shop "${shop.name}" has no products - will show in Featured section`);
          }
          console.log(`❌ Shop "${shop.name}" (ID: ${shop.id}) has no products`);
        }
      } catch (error) {
        if (shop.name.toLowerCase().includes('pet')) {
          console.log(`🐾 ⚠️ Could not check products for Pet Care shop "${shop.name}" (ID: ${shop.id}):`, error);
        }
        if (shop.name.toLowerCase().includes('paradise')) {
          console.log(`🏝️ ⚠️ Could not check products for Paradise shop "${shop.name}" (ID: ${shop.id}):`, error);
        }
        console.log(`⚠️ Could not check products for shop "${shop.name}" (ID: ${shop.id}):`, error);
        // If we can't check, assume it has no products (will show in featured)
      }
    });
    
    await Promise.all(checkPromises);
    setShopsWithProducts(shopsWithProductsSet);
    setShopProducts(productsMap);
    console.log(`✅ Product check complete: ${shopsWithProductsSet.size} shops with products, ${shopsToCheck.length - shopsWithProductsSet.size} shops without products`);
    console.log(`📊 Total shops: ${shopsToCheck.length}`);
    console.log(`📊 Shops with products: ${shopsWithProductsSet.size}`);
    console.log(`📊 Shops without products: ${shopsToCheck.length - shopsWithProductsSet.size}`);
    
    // List all shop names for debugging
    console.log(`📋 All shops:`, shopsToCheck.map(s => `${s.name} (${s.type}, active: ${s.is_active}, has products: ${shopsWithProductsSet.has(s.id)})`));
  };

  useEffect(() => {
    const loadShops = async () => {
      try {
        setIsLoading(true);
        console.log('ShopDiscovery: Loading shops...');
        
        // Try API first
        try {
          const apiShops = await apiService.getShops();
          console.log('🔍 ShopDiscovery: API shops loaded:', {
            count: Array.isArray(apiShops) ? apiShops.length : 0,
            shops: Array.isArray(apiShops) ? apiShops.map((s: any) => ({
              id: s.id,
              name: s.name,
              type: s.type,
              is_active: s.is_active
            })) : []
          });
          if (apiShops && apiShops.length > 0) {
            // Normalize shop data to ensure logo field is properly mapped
            const normalizedApiShops: Shop[] = apiShops.map((shop: any) => {
              // Check all possible image field names (in order of preference)
              const rawLogo = shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || shop.banner_url || undefined;
              return {
                ...shop,
                logo: rawLogo ? getValidImageUrl(rawLogo) : undefined,
                type: normalizeShopType(shop.type)
              };
            });
        setShops(normalizedApiShops);
        setFilteredShops(normalizedApiShops);
        console.log('✅ ShopDiscovery: Set shops from API:', {
          totalShops: normalizedApiShops.length,
          expectedShops: 15,
          missingShops: normalizedApiShops.length < 15 ? `Expected 15 but got ${normalizedApiShops.length}` : 'All shops loaded',
          shopNames: normalizedApiShops.map(s => s.name)
        });
        
        if (normalizedApiShops.length < 15) {
          console.warn('⚠️ WARNING: Only', normalizedApiShops.length, 'shops loaded, but database has 15 shops. Check backend API for pagination/limit issues.');
        }
        
        // Check which shops have products
        checkShopsWithProducts(normalizedApiShops);
        return;
          }
        } catch (apiError) {
          console.warn('API shops not available, using shopService:', apiError);
        }
        
        // Fallback to shopService
        const allShops = await shopService.getShops();
        console.log('ShopDiscovery: Fallback shops loaded:', allShops);
        // Normalize shop types to lowercase for consistent filtering
        const normalizedShops: Shop[] = allShops.map(shop => {
          // Check all possible image field names (in order of preference)
          const rawLogo = shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || shop.banner_url || undefined;
          return {
            ...shop,
            logo: rawLogo ? getValidImageUrl(rawLogo) : undefined,
            type: normalizeShopType(shop.type)
          };
        });
        setShops(normalizedShops);
        setFilteredShops(normalizedShops);
        console.log('ShopDiscovery: Set shops from fallback:', normalizedShops.length);
        
        // Check which shops have products
        checkShopsWithProducts(normalizedShops);
      } catch (error) {
        console.error('Error loading shops:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadShops();
  }, []);

  // Load level 2 categories first (only once)
  useEffect(() => {
    const loadLevel2Categories = async () => {
      try {
        const lvl2Categories = await apiService.getCategoriesForSearch(2);
        setLevel2Categories(lvl2Categories);
        console.log('📦 Loaded level 2 categories:', lvl2Categories.length);
      } catch (error) {
        console.error('Error loading level 2 categories:', error);
      }
    };

    if (level2Categories.length === 0) {
      loadLevel2Categories();
    }
  }, [level2Categories.length]);

  // Load level 3 categories filtered by shop type
  useEffect(() => {
    const loadLevel3Categories = async () => {
      try {
        // If a shop type is selected, find the corresponding level 2 category and filter level 3 by it
        let level2CategoryForFilter: Category | null = null;
        if (selectedType !== 'all' && level2Categories.length > 0) {
          // Map shop type to level 2 category
          const shopTypeLower = selectedType.toLowerCase().trim();
          const keywords: { [key: string]: string[] } = {
            'bakery': ['bakery', 'bakeries', 'bread', 'pastries'],
            'grocery': ['grocery', 'groceries', 'kirana', 'mart'],
            'pharmacy': ['pharmacy', 'medications', 'medicine'],
            'medical': ['medical', 'health', 'wellness'],
            'restaurant': ['restaurant', 'food', 'home food'],
            'specialty': ['specialty', 'organic', 'dry fruits']
          };
          
          const matchingKeywords = keywords[shopTypeLower] || [];
          
          // Find level 2 category that matches the shop type
          level2CategoryForFilter = level2Categories.find(cat => {
            const catName = (cat.name || '').toLowerCase();
            const catTopCategory = (cat.top_category || '').toLowerCase();
            const catDisplayName = (cat.display_name || '').toLowerCase();
            
            return matchingKeywords.some(keyword => 
              catName.includes(keyword) || 
              catTopCategory.includes(keyword) || 
              catDisplayName.includes(keyword) ||
              keyword.includes(catName) ||
              keyword.includes(catTopCategory)
            ) || null;
          }) || null;
          
          console.log('🔍 Level 2 category for shop type:', selectedType, '=', level2CategoryForFilter?.name, level2CategoryForFilter?.id);
        }

        // Load level 3 categories with parent filter if available
        let lvl3Categories: Category[] = [];
        if (level2CategoryForFilter) {
          // Try filtering by parent_id first
          lvl3Categories = await apiService.getCategoriesForSearch(3, level2CategoryForFilter.id, undefined);
          console.log(`📦 Loaded level 3 categories filtered by parent_id (${level2CategoryForFilter.id}):`, lvl3Categories.length);
          
          // If no results, try filtering by parent category name
          if (lvl3Categories.length === 0) {
            lvl3Categories = await apiService.getCategoriesForSearch(3, undefined, level2CategoryForFilter.name);
            console.log(`📦 Loaded level 3 categories filtered by parent name (${level2CategoryForFilter.name}):`, lvl3Categories.length);
          }
        } else {
          // Load all level 3 categories if no shop type filter
          lvl3Categories = await apiService.getCategoriesForSearch(3);
          console.log('📦 Loaded all level 3 categories:', lvl3Categories.length);
        }

        setLevel3Categories(lvl3Categories);

        // Group level 3 categories by parent_id (level 2 category)
        const grouped: { [key: string]: Category[] } = {};
        lvl3Categories.forEach(cat => {
          const parentId = cat.parent_id || cat.top_category || 'all';
          if (!grouped[parentId]) {
            grouped[parentId] = [];
          }
          grouped[parentId].push(cat);
        });
        setCategoriesByParent(grouped);
        console.log('📦 Grouped level 3 categories by parent:', Object.keys(grouped).length, 'groups');
      } catch (error) {
        console.error('Error loading level 3 categories:', error);
      }
    };

    if (level2Categories.length > 0 || selectedType === 'all') {
      loadLevel3Categories();
    }
  }, [selectedType, level2Categories]); // Reload when selectedType or level2Categories changes

  // Removed unused: getLevel2CategoryForShopType
  // Map shop type to level 2 category keywords
  const _getLevel2CategoryForShopType = (shopType: string): Category | null => {
    if (shopType === 'all' || !shopType) return null;
    
    const shopTypeLower = shopType.toLowerCase().trim();
    const keywords: { [key: string]: string[] } = {
      'bakery': ['bakery', 'bakeries', 'bread', 'pastries'],
      'grocery': ['grocery', 'groceries', 'kirana', 'mart'],
      'pharmacy': ['pharmacy', 'medications', 'medicine'],
      'medical': ['medical', 'health', 'wellness'],
      'restaurant': ['restaurant', 'food', 'home food'],
      'specialty': ['specialty', 'organic', 'dry fruits']
    };
    
    const matchingKeywords = keywords[shopTypeLower] || [];
    
    // Find level 2 category that matches the shop type
    const matchingCategory = level2Categories.find(cat => {
      const catName = (cat.name || '').toLowerCase();
      const catTopCategory = (cat.top_category || '').toLowerCase();
      const catDisplayName = (cat.display_name || '').toLowerCase();
      
      return matchingKeywords.some(keyword => 
        catName.includes(keyword) || 
        catTopCategory.includes(keyword) || 
        catDisplayName.includes(keyword) ||
        keyword.includes(catName) ||
        keyword.includes(catTopCategory)
      );
    });
    
    return matchingCategory || null;
  };

  // Update selectedType when initialShopType prop changes
  useEffect(() => {
    if (initialShopType) {
      console.log('🔍 ShopDiscovery: Setting shop type from prop:', initialShopType);
      setSelectedType(initialShopType);
    }
  }, [initialShopType]);

  // Filter shops based on search and selected type
  // Regular section: Only show shops WITH products
  useEffect(() => {
    console.log('ShopDiscovery: Filtering shops by type:', selectedType);
    console.log('ShopDiscovery: Total shops available:', shops.length);
    
    // Debug: Check if Pet Care Center and Paradise are in the shops list
    const petCareShop = shops.find(s => s.name.toLowerCase().includes('pet'));
    if (petCareShop) {
      console.log(`🐾 Pet Care Center found in shops list:`, {
        name: petCareShop.name,
        id: petCareShop.id,
        type: petCareShop.type,
        is_active: petCareShop.is_active,
        hasProducts: shopsWithProducts.has(petCareShop.id)
      });
    } else {
      console.log(`🐾 Pet Care Center NOT found in shops list`);
    }
    
    const paradiseShop = shops.find(s => s.name.toLowerCase().includes('paradise'));
    if (paradiseShop) {
      console.log(`🏝️ Paradise found in shops list:`, {
        name: paradiseShop.name,
        id: paradiseShop.id,
        type: paradiseShop.type,
        is_active: paradiseShop.is_active,
        hasProducts: shopsWithProducts.has(paradiseShop.id),
        willShowIn: shopsWithProducts.has(paradiseShop.id) ? 'Regular' : 'Featured'
      });
    } else {
      console.log(`🏝️ Paradise NOT found in shops list`);
      console.log(`📋 Available shops:`, shops.map(s => `${s.name} (${s.type})`));
    }
    
    // Debug: Check if Raj groceries shop is in the shops list
    const rajShop = shops.find(s => s.name.toLowerCase().includes('raj'));
    if (rajShop) {
      console.log(`🛒 Raj groceries shop found in shops list:`, {
        name: rajShop.name,
        id: rajShop.id,
        type: rajShop.type,
        is_active: rajShop.is_active,
        hasProducts: shopsWithProducts.has(rajShop.id),
        willShowIn: shopsWithProducts.has(rajShop.id) ? 'Regular' : 'Featured',
        matchesTypeFilter: selectedType === 'all' || normalizeShopType(rajShop.type) === normalizeShopType(selectedType),
        matchesSearch: !searchQuery.trim() || rajShop.name.toLowerCase().includes(searchQuery.toLowerCase())
      });
    } else {
      console.log(`🛒 ❌ Raj groceries shop NOT found in shops list`);
      console.log(`📋 Available shops:`, shops.map(s => `${s.name} (${s.type}, active: ${s.is_active})`));
    }
    
    let filtered = shops;

    // Filter by selected type (case-insensitive comparison with normalization)
    if (selectedType !== 'all') {
      filtered = filtered.filter(shop => {
        // Normalize both shop type and selected type for comparison
        const shopTypeNormalized = normalizeShopType(shop.type);
        const selectedTypeNormalized = normalizeShopType(selectedType);
        const matches = shopTypeNormalized === selectedTypeNormalized;
        
        // Also check original values (case-insensitive) for database values like "Bakeries"
        const shopTypeLower = (shop.type || '').toLowerCase().trim();
        const selectedTypeLower = selectedType.toLowerCase().trim();
        const directMatch = shopTypeLower === selectedTypeLower;
        
        // Also check if shop type contains selected type or vice versa (for "Bakeries" vs "bakery")
        const containsMatch = shopTypeLower.includes(selectedTypeLower) || selectedTypeLower.includes(shopTypeLower);
        
        const finalMatch = matches || directMatch || containsMatch;
        
        if (!finalMatch) {
          console.log(`Shop type mismatch: shop.type="${shop.type}" (normalized: "${shopTypeNormalized}") vs selectedType="${selectedType}" (normalized: "${selectedTypeNormalized}")`);
        } else {
          console.log(`✅ Shop type match: shop.type="${shop.type}" matches selectedType="${selectedType}"`);
        }
        return finalMatch;
      });
      console.log(`ShopDiscovery: Filtered ${shops.length} shops to ${filtered.length} shops of type: ${selectedType}`);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(shop =>
        shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        shop.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        shop.address.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Regular section: Only show shops WITH products
    // Wait for product check to complete before filtering (check if we've checked all shops)
    const productCheckComplete = shopsWithProducts.size > 0 || shops.length === 0;
    
    if (productCheckComplete) {
      // Product check has completed, filter by products
      const beforeProductFilter = filtered.length;
      filtered = filtered.filter(shop => shopsWithProducts.has(shop.id));
      console.log(`ShopDiscovery: Filtered to ${filtered.length} shops with products (out of ${beforeProductFilter} after type/search filter, out of ${shops.length} total)`);
      
      // Check if Pet Care Center and Paradise are in filtered results
      const petCareInFiltered = filtered.find(s => s.name.toLowerCase().includes('pet'));
      if (petCareShop && !petCareInFiltered) {
        console.log(`🐾 ⚠️ Pet Care Center filtered out from regular section. Has products: ${shopsWithProducts.has(petCareShop.id)}, Will show in Featured: ${!shopsWithProducts.has(petCareShop.id)}`);
      } else if (petCareInFiltered) {
        console.log(`🐾 ✅ Pet Care Center is in regular section`);
      }
      
      const paradiseInFiltered = filtered.find(s => s.name.toLowerCase().includes('paradise'));
      if (paradiseShop && !paradiseInFiltered) {
        console.log(`🏝️ ⚠️ Paradise filtered out from regular section. Has products: ${shopsWithProducts.has(paradiseShop.id)}, Will show in Featured: ${!shopsWithProducts.has(paradiseShop.id)}`);
      } else if (paradiseInFiltered) {
        console.log(`🏝️ ✅ Paradise is in regular section (has products)`);
      } else if (paradiseShop && !shopsWithProducts.has(paradiseShop.id)) {
        console.log(`🏝️ ✅ Paradise will show in Featured section (no products)`);
      }
      
      // Check if Raj groceries shop is in filtered results
      const rajInFiltered = filtered.find(s => s.name.toLowerCase().includes('raj'));
      if (rajShop && !rajInFiltered) {
        console.log(`🛒 ⚠️ Raj groceries shop filtered out from regular section. Details:`, {
          name: rajShop.name,
          id: rajShop.id,
          type: rajShop.type,
          is_active: rajShop.is_active,
          hasProducts: shopsWithProducts.has(rajShop.id),
          willShowIn: !shopsWithProducts.has(rajShop.id) ? 'Featured' : 'Nowhere (ERROR!)',
          selectedType: selectedType,
          searchQuery: searchQuery,
          typeMatch: selectedType === 'all' || normalizeShopType(rajShop.type) === normalizeShopType(selectedType),
          searchMatch: !searchQuery.trim() || rajShop.name.toLowerCase().includes(searchQuery.toLowerCase())
        });
      } else if (rajInFiltered) {
        console.log(`🛒 ✅ Raj groceries shop is in regular section (has products)`);
      } else if (rajShop && !shopsWithProducts.has(rajShop.id)) {
        console.log(`🛒 ✅ Raj groceries shop will show in Featured section (no products)`);
      }
    } else {
      // Product check not complete yet, show all filtered shops temporarily
      console.log(`ShopDiscovery: Product check in progress (${shopsWithProducts.size}/${shops.length}), showing all ${filtered.length} filtered shops temporarily`);
    }
    
    console.log(`📊 ShopDiscovery Summary:`);
    console.log(`  - Total shops loaded: ${shops.length}`);
    console.log(`  - After type filter (${selectedType}): ${filtered.length}`);
    console.log(`  - Shops with products found: ${shopsWithProducts.size}`);
    console.log(`  - Final regular section shops: ${filtered.length}`);
    
    // Apply favorites filter if selected
    let finalFiltered = filtered;
    if (selectedCategory === 'favorites' && favorites.length > 0) {
      finalFiltered = filtered.filter(shop => favorites.includes(String(shop.id)));
    }
    
    setFilteredShops(finalFiltered);
  }, [shops, selectedType, searchQuery, shopsWithProducts, selectedCategory, favorites]);

  // Get level 3 categories grouped by level 2 parent for filter chips
  // Note: level3Categories are already filtered by API based on selectedType
  const getLevel3CategoriesForFilters = () => {
    // Use the already-filtered level 3 categories from API
    if (level3Categories.length > 0) {
      return level3Categories.map(cat => ({
        id: cat.id,
        name: translateCategoryName(cat.display_name || cat.name),
        icon: cat.icon || '📦',
        parentId: cat.parent_id,
        topCategory: cat.top_category
      }));
    }
    
    // Fallback: Return level 2 categories if no level 3 categories available
    return level2Categories.map(cat => ({
      id: cat.id,
      name: translateCategoryName(cat.display_name || cat.name),
      icon: cat.icon || '📦',
      parentId: cat.parent_id,
      topCategory: cat.top_category
    }));
  };

  // Removed unused: level3CategoriesForFilters

  // Get shop types for backward compatibility (when no categories available)
  const getShopTypes = () => {
    // Normalize types to lowercase for consistent comparison
    const types = new Set(shops.map(shop => (shop.type || '').toLowerCase().trim()).filter(t => t));
    return Array.from(types).map(type => ({
      id: type, // Store lowercase for consistent comparison
      name: type.charAt(0).toUpperCase() + type.slice(1), // Display capitalized
      icon: getShopTypeIcon(type),
      count: shops.filter(shop => (shop.type || '').toLowerCase().trim() === type).length
    }));
  };

  const getShopTypeIcon = (type: string) => {
    const typeLower = (type || '').toLowerCase().trim();
    switch (typeLower) {
      case 'medical': return '🏥';
      case 'grocery': return '🥬';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      case 'bakery': return '🥖';
      case 'restaurant':
      case 'restaurants': return '🍛';
      case 'specialty': return '⭐';
      default: return '🏪';
    }
  };

  // Always use shop types (not level 3 categories)
  const shopTypes = getShopTypes();

  if (isLoading) {
    return (
      <div className="shop-discovery">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>{t.discoveringShops || 'Discovering shops...'}</p>
          </div>
      </div>
    );
  }

  return (
    <div className="shop-discovery">
      {/* Header with Quick Stats */}
      <div className="discovery-header" aria-label="Explore shops header">
        <div className="header-content">
          <div className="header-text">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Category Image or Icon */}
              {categoryInfo?.image ? (
                <img 
                  src={getValidImageUrl(categoryInfo.image)} 
                  alt={categoryInfo.displayName || categoryInfo.name || 'Category'}
                  style={{ 
                    width: '48px', 
                    height: '48px', 
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('.category-icon-fallback')) {
                      const fallback = document.createElement('span');
                      fallback.className = 'category-icon-fallback';
                      fallback.textContent = categoryInfo.icon || '🏪';
                      fallback.style.fontSize = '32px';
                      parent.insertBefore(fallback, target);
                    }
                  }}
                />
              ) : categoryInfo?.icon ? (
                <span style={{ fontSize: '32px' }}>{categoryInfo.icon}</span>
              ) : selectedType !== 'all' ? (
                <span style={{ fontSize: '32px' }}>{shopTypes.find(t => t.id === selectedType)?.icon || '🏪'}</span>
              ) : (
                <span style={{ fontSize: '32px' }}>🏪</span>
              )}
              <h2>
                {categoryInfo?.displayName 
                  ? `${translateCategoryName(categoryInfo.displayName)} ${t.shops || 'Shops'}`
                  : categoryInfo?.name
                  ? `${translateCategoryName(categoryInfo.name)} ${t.shops || 'Shops'}`
                  : selectedType !== 'all' 
                  ? `${translateCategoryName(shopTypes.find(t => t.id === selectedType)?.name || selectedType)} ${t.shops || 'Shops'}`
                  : t.exploreShops || 'Explore Shops'
                }
              </h2>
            </div>
            <p>
              {selectedType !== 'all' 
                ? t.showingShops?.replace('{count}', String(filteredShops.length))
                    .replace('{type}', translateCategoryName(categoryInfo?.displayName || categoryInfo?.name || shopTypes.find(t => t.id === selectedType)?.name || selectedType).toLowerCase())
                    .replace('{plural}', filteredShops.length !== 1 ? 's' : '') || `Showing ${filteredShops.length} ${translateCategoryName(categoryInfo?.displayName || categoryInfo?.name || shopTypes.find(t => t.id === selectedType)?.name || selectedType).toLowerCase()} shop${filteredShops.length !== 1 ? 's' : ''}`
                : t.findNearbyShops || 'Find your nearby and specialty shops with simple filters'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Combined Search and Filter Bar */}
      <CollapsibleSection 
        title={t.searchAndFilters || 'Search & Filters'} 
        icon="🔍"
        defaultExpanded={true}
        className="compact"
      >
        <SmartSearch
          onSearch={setSearchQuery}
          onSelectShop={onSelectShop}
          shops={shops}
          placeholder={t.searchShopsPlaceholder || 'Search shops by name, description, or location...'}
        />
        
        <div className="filter-container">
          {/* Active Filter Badge - Show when a specific type is selected */}
          {selectedType !== 'all' && (
            <div className="active-filter-badge">
              <span className="active-filter-label">
                {shopTypes.find(t => t.id === selectedType)?.icon} 
                Showing: {shopTypes.find(t => t.id === selectedType)?.name} Shops
              </span>
              <button
                className="clear-filter-btn"
                onClick={() => {
                  setSelectedType('all');
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
                aria-label="Clear filter and show all shops"
              >
                ✕ Clear Filter
              </button>
            </div>
          )}
          
          <div className="filter-chips">
            <button
              className={`filter-chip ${selectedCategory === 'all' && selectedType === 'all' && !favorites.length ? 'active' : ''}`}
              onClick={() => {
                // Clear the filter to show all shops on this page
                setSelectedType('all');
                setSelectedCategory('all');
                setSearchQuery('');
              }}
            >
              All ({shops.length})
            </button>
            {favorites.length > 0 && (
              <button
                className={`filter-chip ${favorites.length > 0 && selectedCategory === 'favorites' ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory('favorites');
                  setSelectedType('all');
                  setSearchQuery('');
                }}
              >
                ❤️ Favorites ({favorites.length})
              </button>
            )}
            {shopTypes.map(type => {
              const isActive = selectedType === type.id;
              return (
                <button
                  key={type.id}
                  className={`filter-chip ${isActive ? 'active' : ''} ${selectedType !== 'all' && !isActive ? 'dimmed' : ''}`}
                  onClick={() => {
                    // Always use shop type filtering
                    setSelectedType(type.id);
                    setSelectedCategory('all');
                  }}
                  aria-pressed={isActive}
                >
                  {type.icon} {type.name} ({type.count})
                </button>
              );
            })}
          </div>
        </div>
      </CollapsibleSection>

      {/* Shop Context Label - Show why shops are displayed */}
      {categoryInfo && (categoryInfo.name || categoryInfo.displayName) && (
        <div className="shop-context-label">
          <strong>Showing shops that sell:</strong> {translateCategoryName(categoryInfo.displayName || categoryInfo.name || '')}
        </div>
      )}
      {selectedCategory !== 'all' && !categoryInfo && level3Categories.length > 0 && (
        <div className="shop-context-label">
          <strong>Showing shops that sell:</strong> {(() => {
            const category = level3Categories.find(c => c.id === selectedCategory);
            return category ? translateCategoryName(category.name || category.display_name || '') : '';
          })()}
        </div>
      )}
      {selectedType === 'all' && !categoryInfo && selectedCategory === 'all' && (
        <div className="shop-context-label">
          <strong>Showing all shops</strong> delivering to your location
        </div>
      )}

      {/* Shops Grid */}
      <CollapsibleSection
        title={`${selectedType === 'all' 
          ? 'All Shops' 
          : `${shopTypes.find(t => t.id === selectedType)?.name} Shops`} (${filteredShops.length})`}
        icon={selectedType !== 'all' ? shopTypes.find(t => t.id === selectedType)?.icon || '🏪' : '🏪'}
        defaultExpanded={true}
        className={selectedType !== 'all' ? 'filtered-shops-section' : ''}
      >
        {filteredShops.length === 0 ? (
          <div className="no-shops">
            <div className="no-shops-icon">🔍</div>
            <h4>No shops found</h4>
            <p>
              {selectedType !== 'all' 
                ? `No ${shopTypes.find(t => t.id === selectedType)?.name?.toLowerCase() || selectedType} shops match your search.`
                : 'Try adjusting your search criteria or browse all shops'
              }
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => {
                setSearchQuery('');
                setSelectedType('all');
              }} className="clear-filters-btn">
                Clear Filters
              </button>
              {selectedType !== 'all' && (
                <button onClick={onViewAllShops} className="clear-filters-btn" style={{ background: '#2563eb', color: 'white' }}>
                  View All Shops
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="shops-scroll">
            <div className={`shops-grid show-all`}>
              {filteredShops.map(shop => (
              <div key={shop.id} className="shop-card" aria-label={`Shop card for ${shop.name}`}>
                <div className="shop-card-image">
                  {shop.logo ? (
                    <img 
                      src={shop.logo} 
                      alt={shop.name}
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.shop-icon-fallback')) {
                          const fallback = document.createElement('div');
                          fallback.className = 'shop-icon-fallback';
                          fallback.textContent = getShopTypeIcon(shop.type);
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="shop-icon-fallback">{getShopTypeIcon(shop.type)}</div>
                  )}
                </div>
                
                <div className="shop-card-content">
                  <div className="shop-card-header">
                    <h4 className="shop-card-title">{shop.name}</h4>
                    <div className="shop-status">
                      {shop.is_active ? (
                        <span className="status-active">🟢 {t.open || 'Open'}</span>
                      ) : (
                        <span className="status-inactive">🔴 {t.closed || 'Closed'}</span>
                      )}
                    </div>
                  </div>
                  
                  <p className="shop-card-description">{shop.description}</p>
                  
                  <div className="shop-card-meta">
                    <div className="shop-rating">
                      ⭐⭐⭐⭐⭐ (4.8)
                    </div>
                    <div className="shop-category">
                      {translateShopType(shop.type)}
                    </div>
                  </div>
                  
                  <div className="shop-location">
                    📍 {shop.city}, {shop.state}
                  </div>
                  
                  {/* Product Preview Snippets */}
                  {shopProducts[shop.id] && shopProducts[shop.id].length > 0 && (
                    <div className="shop-product-preview">
                      <div className="shop-product-preview-label">Available:</div>
                      <div className="shop-product-preview-items">
                        {shopProducts[shop.id].map((product: any, idx: number) => (
                          <span key={product.id || idx} className="shop-product-preview-item">
                            {product.name || 'Product'}
                          </span>
                        ))}
                        {shopProducts[shop.id].length >= 3 && (
                          <span className="shop-product-preview-more">+ more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="shop-card-actions">
                  {/* Primary Action: Browse Products - Most common user intent */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Validate shop data before navigation
                      if (!shop || !shop.id) {
                        console.error('❌ ShopDiscovery: Invalid shop data, cannot navigate');
                        return;
                      }
                      
                      console.log('🔍 ShopDiscovery: Browse Products clicked for:', {
                        shopId: shop.id,
                        shopName: shop.name,
                        isActive: shop.is_active
                      });
                      
                      // Navigate to shop page to view ONLY this shop's products
                      onSelectShop(shop);
                    }}
                    className="visit-shop-btn primary-action"
                    disabled={!shop.is_active}
                    title={shop.is_active ? 'View and shop products from this store' : 'Shop is currently closed'}
                  >
                    🛒 {shop.is_active ? (t.browseProducts || 'Browse Products') : (t.temporarilyClosed || 'Temporarily Closed')}
                  </button>
                  
                  {/* Secondary Actions */}
                  <div className="secondary-actions">
                    <button
                      type="button"
                      aria-pressed={favorites.includes(String(shop.id))}
                      aria-label={favorites.includes(String(shop.id)) ? 'Unfavorite shop' : 'Favorite shop'}
                      className={`favorite-btn ${favorites.includes(String(shop.id)) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(String(shop.id));
                      }}
                      title={favorites.includes(String(shop.id)) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {favorites.includes(String(shop.id)) ? '❤️' : '🤍'}
                    </button>
                    {onViewShopDetails && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔍 ShopDiscovery: View Shop Info clicked for:', shop.id, shop.name);
                          onViewShopDetails(shop);
                        }}
                        className="view-details-btn secondary-action"
                        title="View shop information (location, hours, contact, rating)"
                      >
                        ℹ️ {t.shopInfo || 'Shop Info'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
            </div>
            {/* Always show all shops; pagination/toggle can be re-enabled later */}
          </>
        )}
      </CollapsibleSection>

      {/* Featured Shops - Show ALL shops EXCEPT those with products */}
      {(() => {
        // Start with all shops, then apply the same type and search filters as regular section
        let featuredFiltered = shops;
        
        // Apply type filter (same as regular section)
        if (selectedType !== 'all') {
          featuredFiltered = featuredFiltered.filter(shop => {
            const shopTypeNormalized = normalizeShopType(shop.type);
            const selectedTypeNormalized = normalizeShopType(selectedType);
            const matches = shopTypeNormalized === selectedTypeNormalized;
            
            const shopTypeLower = (shop.type || '').toLowerCase().trim();
            const selectedTypeLower = selectedType.toLowerCase().trim();
            const directMatch = shopTypeLower === selectedTypeLower;
            const containsMatch = shopTypeLower.includes(selectedTypeLower) || selectedTypeLower.includes(shopTypeLower);
            
            return matches || directMatch || containsMatch;
          });
        }
        
        // Apply search filter (same as regular section)
        if (searchQuery.trim()) {
          featuredFiltered = featuredFiltered.filter(shop =>
            shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            shop.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            shop.address.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        
        // Filter shops to only include those WITHOUT products (show all except shops with products)
        const featuredShops = featuredFiltered.filter(shop => 
          !shopsWithProducts.has(shop.id)
        );
        
        // Check if Pet Care Center and Paradise are in featured shops
        const petCareInFeatured = featuredShops.find(s => s.name.toLowerCase().includes('pet'));
        if (petCareInFeatured) {
          console.log(`🐾 ✅ Pet Care Center is in Featured Shops section`);
        }
        
        const paradiseInFeatured = featuredShops.find(s => s.name.toLowerCase().includes('paradise'));
        if (paradiseInFeatured) {
          console.log(`🏝️ ✅ Paradise is in Featured Shops section`);
        } else {
          const paradiseShop = shops.find(s => s.name.toLowerCase().includes('paradise'));
          if (paradiseShop) {
            console.log(`🏝️ ⚠️ Paradise NOT in Featured section. Check: hasProducts=${shopsWithProducts.has(paradiseShop.id)}, typeFilter=${selectedType}, searchQuery="${searchQuery}"`);
            console.log(`🏝️ Paradise details:`, {
              name: paradiseShop.name,
              type: paradiseShop.type,
              is_active: paradiseShop.is_active,
              hasProducts: shopsWithProducts.has(paradiseShop.id),
              matchesTypeFilter: selectedType === 'all' || normalizeShopType(paradiseShop.type) === normalizeShopType(selectedType),
              matchesSearch: !searchQuery.trim() || paradiseShop.name.toLowerCase().includes(searchQuery.toLowerCase())
            });
          }
        }
        
        // Check if Raj groceries shop is in featured shops
        const rajInFeatured = featuredShops.find(s => s.name.toLowerCase().includes('raj'));
        if (rajInFeatured) {
          console.log(`🛒 ✅ Raj groceries shop is in Featured Shops section`);
        } else {
          const rajShop = shops.find(s => s.name.toLowerCase().includes('raj'));
          if (rajShop) {
            console.log(`🛒 ⚠️ Raj groceries shop NOT in Featured section. Check:`, {
              name: rajShop.name,
              id: rajShop.id,
              type: rajShop.type,
              is_active: rajShop.is_active,
              hasProducts: shopsWithProducts.has(rajShop.id),
              typeFilter: selectedType,
              searchQuery: searchQuery,
              matchesTypeFilter: selectedType === 'all' || normalizeShopType(rajShop.type) === normalizeShopType(selectedType),
              matchesSearch: !searchQuery.trim() || rajShop.name.toLowerCase().includes(searchQuery.toLowerCase()),
              inFeaturedFiltered: featuredFiltered.find(s => s.id === rajShop.id) ? 'Yes' : 'No'
            });
          }
        }
        
        // Debug logging
        console.log(`📊 Shop Distribution:`);
        console.log(`  - Total shops: ${shops.length}`);
        console.log(`  - Shops with products: ${Array.from(shops).filter(s => shopsWithProducts.has(s.id)).length}`);
        console.log(`  - Shops without products: ${Array.from(shops).filter(s => !shopsWithProducts.has(s.id)).length}`);
        console.log(`  - Regular section (with products): ${filteredShops.length}`);
        console.log(`  - Featured section (all shops except those with products): ${featuredShops.length}`);
        console.log(`  - Total displayed: ${filteredShops.length + featuredShops.length}`);
        
        // Log missing shops
        const displayedShopIds = new Set([
          ...filteredShops.map(s => s.id),
          ...featuredShops.map(s => s.id)
        ]);
        const missingShops = shops.filter(s => !displayedShopIds.has(s.id));
        if (missingShops.length > 0) {
          console.warn(`⚠️ Missing shops (not in either section):`, missingShops.map(s => `${s.name} (${s.id}, type: ${s.type}, active: ${s.is_active}, has products: ${shopsWithProducts.has(s.id)})`));
        }
        
        return featuredShops.length > 0 && (
          <CollapsibleSection 
            title="Featured Shops" 
            icon="⭐"
            defaultExpanded={false}
            className="compact"
          >
            <div className="featured-grid">
              {featuredShops.slice(0, 3).map(shop => (
                <div key={shop.id} className="featured-card">
                  <div className="featured-icon">{getShopTypeIcon(shop.type)}</div>
                  <h5>{shop.name}</h5>
                  <p>{shop.description}</p>
                  <button
                    onClick={() => onSelectShop(shop)}
                    className="featured-btn"
                    disabled={!shop.is_active}
                  >
                    Visit Shop
                  </button>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        );
      })()}
    </div>
  );
};

export default ShopDiscovery;
